<template>
    <div @click.prevent.stop="openFeedbackForm" title="feedback" v-bind="$attrs">
        <feedback-icon class="h-6" />
    </div>
    <teleport to="body">
        <transition name="fade" v-if="preparingFeedbackForm">
            <div
                class="p-10 fixed inset-0 bg-black bg-opacity-80 flex flex-row justify-center items-center z-40"
                @click="closeFeedbackForm"
            >
                <close-button @clicked="closeFeedbackForm" />
                <transition name="fade" mode="out-in">
                    <spinner-message v-if="saving" display-type="centered" message="Saving..." />
                    <div
                        v-else
                        class="w-full xl:w-3/4 bg-white h-full p-16 rounded-lg shadow-2xl overflow-y-auto z-50"
                        @click.stop=""
                    >
                        <div class="flex flex-row justify-start items-end mb-8">
                            <div class="w-1/5">
                                <opus-logo class="w-full" />
                            </div>
                            <div class="w-4/5 pl-10">
                                <h1>
                                    We'd love to hear your feedback on:<br />'<span class="text-primary">{{
                                        sco.title
                                    }}</span
                                    >'
                                </h1>
                            </div>
                        </div>

                        <p class="my-4">
                            If you could please spare a few minutes to leave us a review, we'd be grateful.<br />We hope
                            you enjoyed your training - see you again soon!
                        </p>

                        <v-form @submit="save" :validation-schema="schema">
                            <h2 class="mb-1">How would you rate this training overall?</h2>
                            <p class="text-sm mb-2">(10 being the best)</p>

                            <div class="flex flex-row justify-start">
                                <div
                                    class="flex flex-col justify-center items-center flex-none mx-2 text-2xl"
                                    v-for="i in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
                                    :key="i"
                                >
                                    <label :for="`rating_${i}`" class="inline-block w-12 text-center"> {{ i }}</label>
                                    <v-field
                                        type="radio"
                                        :value="i"
                                        :id="`rating_${i}`"
                                        name="rating"
                                        v-model="feedback.rating"
                                    />
                                </div>
                            </div>
                            <div class="error-message">
                                <error-message name="rating" />
                            </div>

                            <h2 class="mt-8 mb-2">Do you feel this training met your learning needs?</h2>
                            <div class="text-2xl">
                                <label class="mr-4"
                                    >Yes
                                    <v-field
                                        type="radio"
                                        value="yes"
                                        name="learning_needs"
                                        v-model="feedback.learning_needs"
                                /></label>
                                <label
                                    >No
                                    <v-field
                                        type="radio"
                                        value="no"
                                        name="learning_needs"
                                        v-model="feedback.learning_needs"
                                /></label>
                            </div>
                            <div class="error-message">
                                <error-message name="learning_needs" />
                            </div>

                            <h2 class="mt-8 mb-2">Would you recommend this training to someone else?</h2>
                            <div class="text-2xl">
                                <label class="mr-4"
                                    >Yes <v-field type="radio" value="yes" name="recommend" v-model="feedback.recommend"
                                /></label>
                                <label
                                    >No <v-field type="radio" value="no" name="recommend" v-model="feedback.recommend"
                                /></label>
                            </div>
                            <div class="error-message">
                                <error-message name="recommend" />
                            </div>

                            <h2 class="mt-8 mb-2">Anything else you would like to share with us?</h2>
                            <v-field
                                as="textarea"
                                type="textarea"
                                maxlength="255"
                                name="other"
                                v-model="feedback.other"
                                class="form-control h-32"
                            />

                            <div class="mt-4">
                                <button class="btn bg-gray-200 mr-8" @click.prevent="closeFeedbackForm">Cancel</button>
                                <button class="btn btn-primary" type="submit">Save</button>
                            </div>
                        </v-form>
                    </div>
                </transition>
            </div>
        </transition>
    </teleport>
</template>

<script>
import OpusLogo from "@/components/OpusLogo.vue";
import { Form } from "vee-validate";
import { Field, ErrorMessage } from "vee-validate";
import ApiService from "@/services/ApiService";
import FeedbackIcon from "@/components/icons/FeedbackIcon";
import CloseButton from "@/components/utilities/CloseButton.vue";

export default {
    name: "ScoUserFeedback",
    components: {
        OpusLogo,
        VForm: Form,
        VField: Field,
        ErrorMessage,
        FeedbackIcon,
        CloseButton
    },
    props: {
        sco: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            preparingFeedbackForm: false,
            showFeedbackForm: false,
            feedback: {
                rating: null,
                learning_needs: null,
                recommend: null,
                other: null
            },
            schema: {
                rating: value => {
                    if (value) {
                        return true;
                    }

                    return "Please select a rating from 1 to 10";
                },
                learning_needs: value => {
                    if (value && value.length) {
                        return true;
                    }

                    return "Please select 'Yes' or 'No'";
                },
                recommend: value => {
                    if (value && value.length) {
                        return true;
                    }

                    return "Please select 'Yes' or 'No'";
                },
                other: value => {
                    if (value && value.length > 255) {
                        return "Please keep your message shorter than 255 characters.";
                    }

                    return true;
                }
            },
            saving: false
        };
    },
    methods: {
        save() {
            this.saving = true;
            ApiService.postFeedback(this.sco.sco_user_id, this.feedback)
                .then(() => {
                    this.flashSuccess("Thank you for your feedback.", 3000);
                    this.closeFeedbackForm();
                    this.saving = false;
                })
                .catch(() => {
                    this.closeFeedbackForm();
                    this.saving = false;
                });
        },
        openFeedbackForm() {
            this.preparingFeedbackForm = true;
            this.$nextTick(() => {
                this.showFeedbackForm = true;
            });
        },
        closeFeedbackForm() {
            if (this.saving) {
                return;
            }

            this.showFeedbackForm = false;
            this.$nextTick(() => {
                this.preparingFeedbackForm = false;
            });
        }
    }
};
</script>

<style lang="postcss" scoped>
.error-message {
    @apply mt-2 text-red-500;
}
</style>
